import type { ClientPushCategoryUserSetting } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Chip, Text } from 'react-native-paper';

import { useChangePersonFeatureEnabled } from '@/features/api/hooks/shared-feature/useChangePersonFeatureEnabled';
import { useResetPersonFeature } from '@/features/api/hooks/shared-feature/useResetPersonFeature';
import { useChangePushCategorySetting } from '@/features/api/hooks/shared-push/useChangePushCategorySetting';
import { useGetPushCategorySettings } from '@/features/api/hooks/shared-push/useGetPushCategorySettings';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type { PostFeature } from '@/features/feature-management/types/postTypeFeature';
import { ListSection } from '@/features/list/components/ListSection';
import { CategoryItem } from '@/features/profile-setting/components/CategoryItem';
import { OrganizationCommentPushSettings } from '@/features/profile-setting/components/OrganizationCommentPushSettings';
import { PushCategorySectionRecord } from '@/features/profile-setting/constants/pushCategories';
import type { PushCategory } from '@/features/profile-setting/types/pushCategory';
import { PushCategorySection } from '@/features/profile-setting/types/pushCategory';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { GradientBackground } from '@/shared/components/GradientBackground';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const makeStyles = () =>
  StyleSheet.create({
    pageContent: { paddingBottom: 32 },
    titleContainer: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      marginHorizontal: 12
    },
    title: { marginBottom: 20, marginTop: 32 },
    featureChipContainer: {
      flexWrap: 'wrap',
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 12
    }
  });

const CHANNEL_FEATURE_IDENTIFIERS = [
  FeaturesEnum.Gossip,
  FeaturesEnum.NewsItem,
  FeaturesEnum.Happening,
  FeaturesEnum.Suggestion,
  FeaturesEnum.Emergency,
  FeaturesEnum.Offer,
  FeaturesEnum.Seeking,
  FeaturesEnum.SpecialPost
] as const;

const TEMPLATE_VALUE_PATTERN = /\$[^$]+\$/;

const getDisplayableChannelName = (...names: (string | undefined)[]) =>
  names
    .map((name) => name?.trim())
    .find((name): name is string =>
      Boolean(name && !TEMPLATE_VALUE_PATTERN.test(name))
    );

const isConfigurableChannelFeature = (
  featureIdentifier: FeaturesEnum,
  feature: PostFeature
) =>
  feature.featureIdentifier === featureIdentifier &&
  feature.enabledPersonConfigurable !== false &&
  feature.localEnabled !== false;

type FeatureChannel = {
  featureIdentifier: FeaturesEnum;
  feature: PostFeature;
  channelName?: string;
};

const hasChannelName = (
  channel: FeatureChannel
): channel is FeatureChannel & { channelName: string } =>
  Boolean(channel.channelName);

const getFeatureChannelName = (
  featureIdentifier: FeaturesEnum,
  feature: PostFeature,
  pushCategoryName?: string
) => {
  if (!isConfigurableChannelFeature(featureIdentifier, feature)) {
    return;
  }

  return getDisplayableChannelName(
    feature.featureValues?.channelName,
    pushCategoryName
  );
};

export const NotificationSettingScreen: FC = () => {
  const theme = useAppTheme();
  const styles = makeStyles();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.notification-setting'
  });

  const { isRefetching, getFeature, isFeatureEnabled, refetch } = useFeature();

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const { mutateAsync: resetFeature, isPending: isResetFeaturePending } =
    useResetPersonFeature();
  const { mutateAsync: changeFeature, isPending: isChangeFeaturePending } =
    useChangePersonFeatureEnabled();

  const isFeatureLoading =
    isRefetching || isResetFeaturePending || isChangeFeaturePending;

  const handleChangeFeature = useCallback(
    async (featureIdentifier: string, enabled?: boolean) => {
      if (enabled) {
        await changeFeature({
          featureIdentifier,
          featureEnabled: false
        });
      } else {
        await resetFeature({ featureIdentifier });
      }
      refetch();
    },
    [refetch, changeFeature, resetFeature]
  );

  const {
    data: pushCategories,
    isLoading: isCategoryLoading,
    isError,
    refetch: refetchPushCategories
  } = useGetPushCategorySettings();

  const categories: {
    hasCategories: boolean;
    subCategories: PushCategory[];
  } = useMemo(() => {
    const filtered = [...(pushCategories ?? [])]
      .sort((a, b) => a.orderValue - b.orderValue)
      .filter(({ controllingFeatureIdentifier }) => {
        if (!controllingFeatureIdentifier) return true;
        const feature = controllingFeatureIdentifier as FeaturesEnum;
        if (!feature) return true;
        return isFeatureEnabled(feature);
      });
    return {
      hasCategories: hasItems(filtered),
      subCategories: [
        {
          key: PushCategorySection.Posts,
          values: filtered?.filter(
            ({ id }) =>
              PushCategorySectionRecord[id] === PushCategorySection.Posts
          )
        },
        {
          key: PushCategorySection.Groups,
          values: filtered?.filter(
            ({ id }) =>
              PushCategorySectionRecord[id] === PushCategorySection.Groups
          )
        },
        {
          key: PushCategorySection.Miscellaneous,
          values: filtered?.filter(
            ({ id }) =>
              PushCategorySectionRecord[id] !== PushCategorySection.Posts &&
              PushCategorySectionRecord[id] !== PushCategorySection.Groups
          )
        }
      ]
    };
  }, [isFeatureEnabled, pushCategories]);

  const { mutate: changePushSetting, isPending: isChangePushSettingPending } =
    useChangePushCategorySetting();

  const featureChannels = useMemo(
    () =>
      CHANNEL_FEATURE_IDENTIFIERS.map((featureIdentifier) => {
        const feature = getFeature<PostFeature>(featureIdentifier);
        const pushCategoryName = pushCategories?.find(
          ({ controllingFeatureIdentifier }) =>
            controllingFeatureIdentifier === featureIdentifier
        )?.name;
        const channelName = getFeatureChannelName(
          featureIdentifier,
          feature,
          pushCategoryName
        );

        return { featureIdentifier, feature, channelName };
      }).filter((channel) => hasChannelName(channel)),
    [getFeature, pushCategories]
  );

  const handleChangePushSetting = useCallback(
    (categoryId: string, loudPushEnabled: boolean) =>
      changePushSetting({ categoryId, loudPushEnabled }),
    [changePushSetting]
  );

  const toListItem = useCallback(
    (pushCategory: ClientPushCategoryUserSetting) => (
      <CategoryItem
        key={pushCategory.id}
        category={pushCategory}
        onChange={handleChangePushSetting}
      />
    ),
    [handleChangePushSetting]
  );

  const handleRefetch = useCallback(() => {
    refetch();
    refetchPushCategories();
  }, [refetch, refetchPushCategories]);

  return (
    <GradientBackground>
      <StateView
        isLoading={isCategoryLoading}
        isError={isError}
        stateLabels={t('state', { returnObjects: true })}
        onRefetch={handleRefetch}
        backgroundColor="transparent"
        contentContainerStyle={styles.pageContent}
      >
        <View style={styles.titleContainer}>
          <Text style={styles.title} variant="titleLarge">
            {t('channels')}
          </Text>
          {isFeatureLoading && <ActivityIndicator />}
        </View>
        <View style={styles.featureChipContainer}>
          {featureChannels.map(
            ({ featureIdentifier, feature: { enabled }, channelName }) => (
              <Chip
                key={featureIdentifier}
                mode={enabled ? 'flat' : 'outlined'}
                selected={enabled}
                selectedColor={theme.colors.onPrimary}
                onPress={() => {
                  if (isFeatureLoading) return;
                  handleChangeFeature(featureIdentifier, enabled);
                }}
                style={{
                  backgroundColor: enabled ? theme.colors.primary : undefined,
                  borderColor: enabled ? undefined : theme.colors.outlineVariant
                }}
                textStyle={{
                  color: enabled
                    ? theme.colors.onPrimary
                    : theme.colors.onSurface
                }}
              >
                {channelName}
              </Chip>
            )
          )}
        </View>
        {categories.hasCategories && (
          <>
            <View style={styles.titleContainer}>
              <Text style={styles.title} variant="titleLarge">
                {t('notifications')}
              </Text>
              {isChangePushSettingPending && <ActivityIndicator />}
            </View>
            {categories.subCategories.map(
              ({ key, values }) =>
                hasItems(values) && (
                  <ListSection
                    key={key}
                    title={t(key)}
                    style={{ backgroundColor: 'transparent' }}
                    headerBackgroundColor="transparent"
                  >
                    {values?.map((pushCategory) => toListItem(pushCategory))}
                  </ListSection>
                )
            )}
          </>
        )}
        <OrganizationCommentPushSettings />
      </StateView>
    </GradientBackground>
  );
};
